import { toast } from "@/app/_providers/toast";
import { translateWithFallback } from "@/app/_utilities/translateWithFallback";
import { createVideoThumbnail, getMediaDuration } from "@/app/_utilities/videoUtils";
import { logger } from "@utilities/logger";
import { useCallback, useState } from "react";
import { useHeroSubtitleMediaTranslations } from "./HeroSubtitleMediaTranslations";
import { Subtitles } from "@utilities/subtitles/subtitleTypes";
import { convertSRTToWebVTT, createWebVTTDataUrl } from "@utilities/subtitles/subtitleConversion";
import { useTranslations } from "next-intl";
import { LanguageOrAccent } from "@mw/shared/languagesAndAccents";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { env } from "@/env";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

const SERVER_URL = env.NEXT_PUBLIC_XI_SERVER_URL;

export type MediaInfo = {
  videoUrl: string;
};

export interface useHeroSubtitleMediaProps {
  demoVideo: MediaInfo;
  demoSubtitles?: Subtitles;
}

export type SubtitleMediaState =
  | {
      type: "demo";
      video: MediaInfo;
      subtitles?: Subtitles;
    }
  | {
      type: "uploaded";
      file: File;
      fileThumbnail: string;
      video: MediaInfo;
      subtitles?: undefined;
    }
  | {
      type: "generating";
      file: File;
      fileThumbnail: string;
      video: MediaInfo;
      subtitles?: undefined;
    }
  | {
      type: "complete";
      file: File;
      fileThumbnail: string;
      video: MediaInfo;
      subtitles: Subtitles;
      subtitleId: string;
      srtText: string;
    }
  | {
      type: "quota_exceeded";
      video: MediaInfo;
      file: File;
      fileThumbnail: string;
      subtitles?: undefined;
    }
  | {
      type: "duration_exceeded";
      video: MediaInfo;
      file: File;
      fileThumbnail: string;
      subtitles?: undefined;
    };

export const MAX_UPLOAD_DURATION_MINUTES = 10;
export const MAX_UPLOAD_SIZE_MB = 50;
export const BYTES_PER_MB = 1024 * 1024;
export const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * BYTES_PER_MB;

export function useHeroSubtitleMedia({ demoVideo, demoSubtitles }: useHeroSubtitleMediaProps) {
  const [state, setState] = useState<SubtitleMediaState>({
    type: "demo",
    video: demoVideo,
    subtitles: demoSubtitles,
  });
  const t = useHeroSubtitleMediaTranslations();
  const tl = useTranslations("ClientKeys.LanguagesAndAccents");

  const uploadVideo = useCallback(
    async (file: File) => {
      let fileThumbnail: string | null = null;
      try {
        // Create object URL for the uploaded file (needed for display)
        const videoUrl = URL.createObjectURL(file);
        // Create thumbnail from video
        fileThumbnail = await createVideoThumbnail(file);
        const video = {
          videoUrl,
        };
        let durationSeconds: number | null = null;
        try {
          durationSeconds = await getMediaDuration(file);
        } catch (durationError) {
          // If we can't determine duration, proceed anyway (some files may not have duration metadata)
          logger.warn("Could not determine media duration:", durationError);
        }

        captureEvent(EventName.mw_subtitle_media_file_uploaded, {
          file_size_bytes: file.size,
          file_duration_minutes: durationSeconds ? Math.round(durationSeconds / 60) : undefined,
        });
        if (durationSeconds && durationSeconds > MAX_UPLOAD_DURATION_MINUTES * 60) {
          setState({
            type: "duration_exceeded",
            file,
            fileThumbnail,
            video,
          });
          captureEvent(EventName.mw_subtitle_media_limit_shown, {
            limit_type: "duration",
            max_duration_minutes: MAX_UPLOAD_DURATION_MINUTES,
          });
          return;
        }

        setState({
          type: "uploaded",
          file,
          fileThumbnail,
          video,
        });
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Failed to create thumbnail");
        toast.error(t("widget.toasts.process_failed"));
        if (fileThumbnail) {
          URL.revokeObjectURL(fileThumbnail);
        }
      }
    },
    [t],
  );

  const generate = useCallback(
    async (subtitleLanguage?: LanguageOrAccent) => {
      if (state.type !== "uploaded") {
        toast.error(t("widget.toasts.no_file_uploaded"));
        logger.error(null, t("widget.toasts.no_file_uploaded"));
        return;
      }

      const { file, fileThumbnail, video } = state;

      try {
        captureEvent(EventName.mw_subtitle_media_generation_started, {
          subtitle_lang: subtitleLanguage?.value || "auto",
        });
        // Update state to generating
        setState(prev => {
          if (prev.type !== "uploaded") return prev;
          return {
            type: "generating",
            file: prev.file,
            fileThumbnail: prev.fileThumbnail,
            video: prev.video,
          };
        });

        // Call our Next.js API route which will forward to the external API
        const formData = new FormData();
        formData.append("file", file);
        if (subtitleLanguage) formData.append("subtitle_language", subtitleLanguage.value);
        const token = await getCaptchaToken({ behaviourType: "subtitles" });
        formData.append("hcaptcha_token", token);

        const response = await fetch(`${SERVER_URL}/v1/subtitles/anonymous`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          if (error.detail?.message === "quota_exceeded" || error.detail === "quota_exceeded") {
            captureEvent(EventName.mw_subtitle_media_limit_shown, {
              limit_type: "quota",
            });
            setState({
              type: "quota_exceeded",
              video,
              file,
              fileThumbnail,
            });
            return;
          }
          toast.error(t("widget.toasts.subtitles_failed_title"));
          logger.error(null, error.detail?.message || error.message || "Failed to generate subtitles");
          setState(prev => {
            if (prev.type !== "generating") return prev;
            return {
              ...prev,
              type: "uploaded",
            };
          });
          return;
        }

        const responseData = await response.json();
        const { subtitle_id, srt_content } = responseData;

        // Convert SRT to WebVTT for display in video player
        const webvttText = convertSRTToWebVTT(srt_content);
        // Create a data URL (base64 encoded) for the WebVTT file
        // Using data URL instead of blob URL because blob URLs don't always work with <track> elements
        // Uses Unicode-safe Base64 encoding to handle non-ASCII characters
        const webvttDataUrl = createWebVTTDataUrl(webvttText);

        // Update state to complete
        setState({
          type: "complete",
          file,
          fileThumbnail,
          video: {
            videoUrl: video.videoUrl,
          },
          subtitles: {
            label: subtitleLanguage?.label ? translateWithFallback(tl, subtitleLanguage.label) : "English",
            srcLang: subtitleLanguage?.value || "en",
            src: webvttDataUrl,
          },
          subtitleId: subtitle_id,
          srtText: srt_content,
        });
        toast.success(t("widget.toasts.subtitles_success"));
        captureEvent(EventName.mw_subtitle_media_generation_completed, {
          subtitle_lang: subtitleLanguage?.value || "auto",
        });
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "Subtitle generation error");
        toast.error(t("widget.toasts.subtitles_failed_title"), {
          description:
            error instanceof Error ? error.message : t("widget.toasts.subtitles_failed_description_fallback"),
        });
        // Reset to uploaded state on error
        setState({
          type: "uploaded",
          file,
          fileThumbnail,
          video,
        });
        captureEvent(EventName.mw_subtitle_media_generation_error, {
          subtitle_lang: subtitleLanguage?.value || "auto",
        });
      }
    },
    [state, t, tl],
  );

  const reset = useCallback(() => {
    if (state.type === "demo") return;

    URL.revokeObjectURL(state.video.videoUrl);
    if ("fileThumbnail" in state) {
      URL.revokeObjectURL(state.fileThumbnail);
    }

    setState({
      type: "demo",
      video: demoVideo,
      subtitles: demoSubtitles,
    });
  }, [demoVideo, demoSubtitles, state]);

  return {
    state,
    uploadVideo,
    generate,
    reset,
  };
}
